import {
  type CareOrder,
  type Currency,
  type FundDefinition,
  getGroup,
  isCompound,
  isOption,
  isSpot,
  OrdTypeEnum,
  type PositionUpdateSourceEnum,
  type ProductTypeEnum,
  type QuoteMarket,
  RFQTradingProtocolEnum,
  type Security,
  SideEnum,
} from '@talos/kyoko';
import type { PrimeRFQParams, RFQCurrency } from './types';

export function careOrderToPrimeRFQParams(careOrder: CareOrder): PrimeRFQParams {
  return {
    group: careOrder.Group,
    symbol: careOrder.Symbol,
    side: undefined,
    currency: careOrder.Currency,
    orderQty: careOrder.LeavesQty,
    parent: {
      id: careOrder.OrderID,
      type: careOrder.isNetted ? 'working-order' : 'care-order',
    },
    externalID: careOrder.ExternalGroup,
    externalComments: careOrder.ExternalComments,
    settleDate: careOrder.SettleDate,
    subAccount: careOrder.SubAccount,
    defaultToEmptySubAccount: true, // If the subAccount value above is undefined, the rfq form should default to empty

    rfqTradingProtocol: RFQTradingProtocolEnum.Negotiated, // Care order-primed RFQs are always defaulted to the "Negotiated" protocol
  };
}

/** string | 0 -> string | undefined */
export function stringRFQCurrency(currency: RFQCurrency | undefined): string | undefined {
  return typeof currency === 'string' ? currency : undefined;
}

export function showRFQFormTotalInHomeCcy(security: Security | undefined, homeCcy: Currency | undefined): boolean {
  if (!security || !homeCcy) {
    return false;
  }

  const homeCcyIsBaseOrQuote = [security.BaseCurrency, security.QuoteCurrency].includes(homeCcy.Symbol);
  if (homeCcyIsBaseOrQuote && isSpot(security)) {
    return false;
  }

  if (isCompound(security) || isOption(security)) {
    return false;
  }

  return true;
}

/** Returns whether or not the provided list of securities contains multiple product groups */
export function hasMultipleProductGroups(securities: Security[]): boolean {
  const foundGroups = new Set<string>();
  for (const security of securities) {
    const group = getGroup(security);
    foundGroups.add(group);
    if (foundGroups.size > 1) {
      return true;
    }
  }

  return false;
}

const FALCONX_MARKET_NAME = 'falconx';

export function isFalconXOption(security: Security | undefined): boolean {
  return (isOption(security) && security?.Markets?.includes(FALCONX_MARKET_NAME)) ?? false;
}

interface ShouldDeArmTradeBestParams {
  ordType: OrdTypeEnum | undefined;
  /** The currently armed market to be sent if we click again */
  armedMarket: string | undefined;
  /** Upon new Quote revision, the new best market (might be different to armedMarket) */
  currentBestMarket: string | undefined;
}

/** Reusable definition of logic for deciding if we should de-arm an armed trade best button (double click to trade) */
export function shouldDeArmTradeBest({ ordType, armedMarket, currentBestMarket }: ShouldDeArmTradeBestParams): boolean {
  if (ordType !== OrdTypeEnum.RFQ || armedMarket == null) {
    return false;
  }

  return armedMarket !== currentBestMarket;
}

/** In the RFQSlice, we create a static Position request. */
export const RFQSLICE_POSITION_REQUEST_PARAMS: { AssetTypes: ProductTypeEnum[]; Source: PositionUpdateSourceEnum } = {
  AssetTypes: ['Option'], // we only support FalconX options for now in the RFQ form. As we add support for more types, we should add those here.
  Source: 'Gateway',
};

/** A fund is selectable if it has tradable securities defined and with at least one entry */
export function isFundSelectableInRFQForm(fund: FundDefinition): boolean {
  return fund.TradableSecurities != null && fund.TradableSecurities.length > 0;
}

export function getOrderQtyForQuoteAcceptance({
  side,
  childQuote,
  selectedOrderQty,
  orderQty,
}: {
  side: SideEnum;
  childQuote: Pick<QuoteMarket, 'BidQty' | 'OfferQty'>;
  selectedOrderQty: string;
  /** Explicit accept quantity from partial-fill/sweeper flows. */
  orderQty?: string;
}): string {
  if (orderQty) {
    return orderQty;
  }

  // For size countering flows, venues can return side-specific tradable qty on the quote leg.
  // We prefer that qty, but fall back to the user-entered RFQ qty when leg qty is missing.
  return side === SideEnum.Buy
    ? (childQuote.OfferQty ?? selectedOrderQty)
    : side === SideEnum.Sell
      ? (childQuote.BidQty ?? selectedOrderQty)
      : selectedOrderQty;
}

/**
 * Size countering is supported for Spot, and for Compound only when the instrument has multiple legs.
 * Single-token Compound instruments are intentionally excluded.
 */
export function securitySupportsSizeCountering(security: Security | undefined): boolean {
  if (!security) {
    return false;
  }

  if (isSpot(security)) {
    return true;
  }

  if (isCompound(security)) {
    return (security.Legs?.length ?? 0) > 1;
  }

  return false;
}

export function securityHasSizeCounteringFeatureFlagEnabled(
  security: Security | undefined,
  {
    enableRFQSizeCounteringMultitokenCompound,
    enableRFQSizeCounteringSpot,
  }: {
    enableRFQSizeCounteringMultitokenCompound: boolean;
    enableRFQSizeCounteringSpot: boolean;
  }
): boolean {
  if (!securitySupportsSizeCountering(security)) {
    return false;
  }

  if (isSpot(security)) {
    return enableRFQSizeCounteringSpot;
  }

  if (isCompound(security)) {
    return enableRFQSizeCounteringMultitokenCompound;
  }

  return false;
}
